import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { Bundle } from '@adornis/buchungsheld-db/entities/Bundle.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { dataviewEmptyState, formatPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/filter/client/x-filter-editor.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { Price } from '@adornis/payments/db/prices.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { watch } from '@lit-labs/preact-signals';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { useContextEntity } from '../../../../util.js';
import './bh-page-admin-einrichtung-bundle-modify.js';

const getLsKey = (institutionID: string) => `extra_columns:${institutionID}:bundles:v1`;

function getCachedExtraColumns(institutionID: string) {
  const lsKey = getLsKey(institutionID);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, columns: string[]) {
  const lsKey = getLsKey(institutionID);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

function onViewUpdate(institutionID: string, dataViewOptions: any, view: View<any>) {
  const preExistingFields = dataViewOptions.customColumns?.map(col => col.fieldName).filter(isDefined) ?? [];
  const cachedExtraFields = getCachedExtraColumns(institutionID);
  const selectionFields = view.configuredColumns?.map(col => col.fieldName).filter(isDefined) ?? [];
  const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
  const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
  const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
  for (const field of newFields) newCachedExtraFields.push(field);

  setCachedExtraColumns(institutionID, newCachedExtraFields);

  view.configuredColumns?.sort((col1, col2) => {
    if (col1.customID === '_select') return -1;
    if (col1.customID === '_delete' || col1.customID === '_edit') return 1;
    if (col2.customID === '_select') return 1;
    if (col2.customID === '_delete' || col2.customID === '_edit') return -1;
    return 0;
  });
}

export const adminEinrichtungBundlesRouter = makeRouter(
  {
    root: {
      path: '/',
      metaData: {
        semesterSelect: true,
        breadcrumbs: ({ translate }) => [{ content: translate('bundle', 'plural'), active: true }],
      },
      render: params => renderRoot(params),
    },
    add: {
      path: '/add',
      metaData: {
        breadcrumbs: ({ translate, params }) => [
          {
            content: translate('bundle', 'plural'),
            href: `/admin/institution-details/${params.institutionID}/bundles`,
          },
          { content: translate('add'), active: true },
        ],
      },
      render: params => renderAdd(params),
    },
    edit: {
      path: '/edit/:bundleID',
      metaData: {
        breadcrumbs: ({ translate, params }) => [
          {
            content: translate('bundle', 'plural'),
            href: `/admin/institution-details/${params.institutionID}/bundles`,
          },
          { content: translate('edit'), active: true },
        ],
      },
      render: params => renderEdit(params),
    },
  },
  { fallback: () => html` <x-empty-placeholder .img=${'close'} .text=${'404'} no-wrapper></x-empty-placeholder> ` },
);

async function saveFilter(
  viewSettings: Maybe<
    NarrowedEntity<AdminInstitutionViewSetting, { institutionDetailsBundlesFilterV1: typeof BHFilter.allFields }>
  >,
  filter: any,
) {
  if (!viewSettings) return;
  viewSettings.institutionDetailsBundlesFilterV1 = filter;
  await viewSettings.save();
}

const renderRoot = stateful(params => {
  const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({
    _id: 1,
    selectedTimeframeID: 1,
    institutionDetailsBundlesFilterV1: selectionSet(() => BHFilter, 5),
  });
  const institutionID = params.institutionID;
  const selectedTimeframeID = viewSettings?.selectedTimeframeID;
  const design = useObservable(() => DesignSystem.currentTheme, []);
  // FIXME: this currently leed to a rerender loop
  // const exposeAction = useExposeActions();

  if (!institutionID) return html` <tr-loading-state></tr-loading-state> `;

  // Get the cached extra columns
  const extraColumns = getCachedExtraColumns(institutionID).map(fieldName => ({ fieldName }));

  // Create the filter with base properties
  const filter = new BHFilter({
    institutionID: institutionID,
    timeframeID: selectedTimeframeID,
  });

  // Get saved filter values
  const savedAspects = viewSettings?.institutionDetailsBundlesFilterV1?.aspects;
  const savedSortFields = viewSettings?.institutionDetailsBundlesFilterV1?.sortingFields;
  const savedSortDirections = viewSettings?.institutionDetailsBundlesFilterV1?.sortingDirections;

  // Only set values if they exist and have length > 0
  if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
  if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
  if (savedSortDirections && savedSortDirections.length > 0) filter.sortingDirections = savedSortDirections as any;

  const { dataViewOptions, filterOptions } = buildFullOptions({
    class: Bundle,
    operation: 'subscribeAllBundlesByInstitution_prop',
    view: new View({ filter }),
    onViewChanged: async view => {
      onViewUpdate(institutionID, dataViewOptions, view);
      // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
      // await saveFilter(viewSettings, view.filter);
    },
    isSortable: true,
    isColumnSelectable: true,
    emptyState: () => dataviewEmptyState({}),
    customColumns: [
      {
        columnName: clientTranslator.translate('field', 'name'),
        fieldName: 'name',
        isAlwaysVisible: true,
        selectionSet: Bundle.allFields,
      },
      {
        columnName: clientTranslator.translate('field', 'unitAmount'),
        fieldName: 'price.unitAmount',
        renderItem: instance => html` <tr-text>${formatPrice(instance.getUnitAmount)}</tr-text> `,
        isAlwaysVisible: true,
      },
      {
        columnName: clientTranslator.translate('field', 'productCount'),
        fieldName: 'productIDs',
        renderItem: instance => html` <tr-text>${instance.productIDs.length}</tr-text> `,
        isAlwaysVisible: true,
      },
      ...extraColumns,
      {
        columnName: '',
        fieldName: 'isActive',
        columnWidth: 'max-content',
        renderItem: instance => html`
          <tr-text tone=${instance.isActive ? 'accent' : 'placeholder'} ${css({ textAlign: 'end' })}>
            ${instance.isActive
              ? clientTranslator.translate('field', 'active')
              : clientTranslator.translate('field', 'inactive')}
          </tr-text>
        `,
        isAlwaysVisible: true,
      },
      {
        columnName: '',
        customID: '_edit',
        isAlwaysVisible: true,
        columnWidth: 'max-content',
        renderItem: instance =>
          html`
            <tr-button-small
              icon="edit"
              .action=${() => goTo(`/admin/institution-details/${institutionID}/bundles/edit/${instance._id}`)}
              .color=${design?.colors.secondary.shade(200)}
            ></tr-button-small>
          `,
      },
      {
        columnName: '',
        customID: '_delete',
        isAlwaysVisible: true,
        columnWidth: 'max-content',
        renderItem: instance =>
          html`
            <tr-button-small
              icon="delete"
              .action=${async () => {
                if (await XDialog.confirm(clientTranslator.translate('bundle', 'confirm_deletion')))
                  return instance.remove();
              }}
              .color=${design?.colors.tone.error}
            ></tr-button-small>
          `,
      },
    ],
  });

  // exposeAction.expose('addBundle', {
  //   icon: 'add',
  //   callback: () => goTo(`/admin/institution-details/${institutionID}/bundles/add`),
  // });

  return html`
    <tr-flex space="md">
      <x-button
        @click=${() => goTo(`/admin/institution-details/${institutionID}/bundles/add`)}
        ${css({ width: 'min-content' })}
        >Bundle hinzufügen</x-button
      >
      <x-filter-editor
        .options=${filterOptions}
        @filter-apply=${e => saveFilter(viewSettings, e.detail)}
        @filter-reset=${() => saveFilter(viewSettings, null)}
      ></x-filter-editor>
      <x-data-view .options=${dataViewOptions}></x-data-view>
    </tr-flex>
  `;
});

const renderEdit = stateful(params => {
  const { value: timeframes, loading: timeframesLoading } = useQuery(
    () => Timeframe.getTimeframesByInstitution(params.institutionID)(Timeframe.allFields),
    [params.institutionID],
  );
  const { value: bundle } = useQuery(
    () =>
      getByID(
        Bundle,
        params.bundleID,
      )({
        name: 1,
        price: Price.allFields,
        timeframeID: 1,
        productIDs: 1,
        institutionID: 1,
        isActive: 1,
      }),
    [params.bundleID],
  );

  if (timeframesLoading || !bundle) return html`<x-loading-state></x-loading-state>`;
  return html`
    <bh-page-admin-einrichtung-bundle-modify
      institution-id=${params.institutionID}
      .timeframes=${timeframes}
      .bundle=${bundle}
      mode="edit"
    ></bh-page-admin-einrichtung-bundle-modify>
  `;
});

@customElement('bh-page-admin-einrichtung-bundles')
export class BHPageAdminEinrichtungBundles extends BuchungsheldLitElement {
  override render() {
    return html` ${watch(adminEinrichtungBundlesRouter.outlet)} `;
  }
}

const renderAdd = stateful(params => {
  const { value, loading, error } = useQuery(
    () => Timeframe.getTimeframesByInstitution(params.institutionID)(Timeframe.allFields),
    [params.institutionID],
  );

  if (loading) return html`<x-loading-state></x-loading-state>`;

  return html`
    <bh-page-admin-einrichtung-bundle-modify
      institution-id=${params.institutionID}
      .timeframes=${value}
      .bundle=${new Bundle({
        institutionID: params.institutionID,
        timeframeID: value?.[0]?._id,
        timeframe: value?.[0],
      })}
      mode="add"
    ></bh-page-admin-einrichtung-bundle-modify>
  `;
});
